import { AlertCircle, ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { IconName } from '../../../components/IconSelector';
import IconSelector from '../../../components/IconSelector';
import { InlineCardEditor } from '../../../components/InlineCardEditor';
import MultiSelectDropdown from '../../../components/MultiSelectDropdown';
import type {
  CardAction,
  Card as CardType,
  CreateGoalPayload,
  Goal,
  Recurrence,
  SeriesConfigSelector,
  User,
} from '../../../service/api';
import api, { toCardActionPayload } from '../../../service/api';
import useManagerGoalsStore from '../../../store/goals';

type GoalFormData = Omit<CreateGoalPayload, 'config' | 'rewardCard'> & {
  config: Omit<CreateGoalPayload['config'], 'amount'> & {
    amount: number | null;
  };
  rewardCard: CardAction[];
};

export default function CreateGoalPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: goalId } = useParams<{ id: string }>();
  const isEditMode = !!goalId;
  const copyFromData = location.state?.copyFrom;
  const { updateGoal, deleteGoal } = useManagerGoalsStore();

  const [isLoadingGoal, setIsLoadingGoal] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [users, setUsers] = useState<User[]>([]);
  const [recurrences, setRecurrences] = useState<Recurrence[]>([]);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [cards, setCards] = useState<CardType[]>([]);

  const [formData, setFormData] = useState<GoalFormData>({
    title: '',
    description: '',
    icon: 'trophy',
    assignedTo: [],
    config: {
      selectors: [
        {
          type: 'task',
          task: '',
        },
      ],
      rule: 'any_of',
      sequenceType: 'cumulative',
      amount: 1,
      repeatable: false,
    },
    rewardCard: [{ type: 'sticker', amount: 10 }],
  });

  useEffect(() => {
    fetchRequiredData();
    if (isEditMode) {
      fetchGoal();
    } else if (copyFromData) {
      // Handle copied data
      const copiedGoal = copyFromData;

      // Handle both old and new schema for backward compatibility
      const selectors = copiedGoal.config.selectors || [
        (copiedGoal.config as unknown as { selector: SeriesConfigSelector })
          .selector,
      ];
      const mappedSelectors = selectors.map(
        (selector: SeriesConfigSelector) => {
          if (selector.type === 'task') {
            // Handle both ObjectId string and populated object
            const taskId =
              typeof selector.task === 'string'
                ? selector.task
                : (selector.task as { _id: string } | null)?._id || '';
            return { type: 'task' as const, task: taskId };
          } else if (selector.type === 'achievement') {
            // Handle both ObjectId string and populated object
            const achievementId =
              typeof selector.achievement === 'string'
                ? selector.achievement
                : (selector.achievement as { _id: string } | null)?._id || '';
            return { type: 'achievement' as const, achievement: achievementId };
          } else if (selector.type === 'card') {
            const cardId =
              typeof selector.card === 'string'
                ? selector.card
                : (selector.card as { _id: string } | null)?._id || '';
            return { type: 'card' as const, card: cardId };
          }
          return selector;
        },
      );

      const rewardCard: CardAction[] =
        typeof copiedGoal.rewardCard === 'string'
          ? [{ type: 'sticker', amount: 10 }]
          : copiedGoal.rewardCard?.actions || [{ type: 'sticker', amount: 10 }];

      setFormData({
        title: `${copiedGoal.title} (Copy)`,
        description: copiedGoal.description,
        icon: copiedGoal.icon,
        assignedTo: copiedGoal.assignedTo.map((user: User) => user._id),
        config: {
          selectors: mappedSelectors,
          rule: copiedGoal.config.rule || 'any_of',
          sequenceType: copiedGoal.config.sequenceType,
          amount: copiedGoal.config.amount,
          repeatable: copiedGoal.config.repeatable,
        },
        rewardCard,
      });
    }
    // eslint-disable-next-line
  }, [isEditMode, goalId, copyFromData]);

  const fetchRequiredData = async () => {
    setIsLoadingData(true);
    try {
      const [allUsers, allRecurrences, allGoals, allCards] = await Promise.all([
        api.usersGetAll(),
        api.recurrencesGetAll(),
        api.goals.getAll(),
        api.cards.getAll(),
      ]);

      setUsers(allUsers);
      setRecurrences(allRecurrences);
      setGoals(allGoals?.map(g => g.goal) || []);
      setCards(allCards || []);
    } catch {
      setError('Failed to load required data');
    } finally {
      setIsLoadingData(false);
    }
  };

  const fetchGoal = async () => {
    if (!goalId) return;
    setIsLoadingGoal(true);
    try {
      const { goal } = await api.goals.getById(goalId);

      // Handle both old and new schema for backward compatibility
      const selectors = goal.config.selectors || [
        (goal.config as unknown as { selector: SeriesConfigSelector }).selector,
      ];
      const mappedSelectors = selectors.map(
        (selector: SeriesConfigSelector) => {
          if (selector.type === 'task') {
            // Handle both ObjectId string and populated object
            const taskId =
              typeof selector.task === 'string'
                ? selector.task
                : (selector.task as { _id: string } | null)?._id || '';
            return { type: 'task' as const, task: taskId };
          } else if (selector.type === 'achievement') {
            // Handle both ObjectId string and populated object
            const achievementId =
              typeof selector.achievement === 'string'
                ? selector.achievement
                : (selector.achievement as { _id: string } | null)?._id || '';
            return { type: 'achievement' as const, achievement: achievementId };
          } else if (selector.type === 'card') {
            const cardId =
              typeof selector.card === 'string'
                ? selector.card
                : (selector.card as { _id: string } | null)?._id || '';
            return { type: 'card' as const, card: cardId };
          }
          return selector;
        },
      );

      const rewardCard: CardAction[] =
        typeof goal.rewardCard === 'string'
          ? [{ type: 'sticker', amount: 10 }]
          : goal.rewardCard?.actions || [{ type: 'sticker', amount: 10 }];

      setFormData({
        title: goal.title,
        description: goal.description,
        icon: goal.icon,
        assignedTo: goal.assignedTo?.map((user: User) => user._id) || [],
        config: {
          selectors: mappedSelectors,
          rule: goal.config.rule || 'any_of',
          sequenceType: goal.config.sequenceType,
          amount: goal.config.amount,
          repeatable: goal.config.repeatable,
        },
        rewardCard,
      });
    } catch {
      setError('Failed to load goal');
    } finally {
      setIsLoadingGoal(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const payload: CreateGoalPayload = {
        ...formData,
        config: {
          ...formData.config,
          amount: formData.config.amount ?? 1,
        },
        rewardCard: toCardActionPayload(formData.rewardCard),
      };

      if (isEditMode) {
        await updateGoal(goalId!, payload);
      } else {
        await api.goals.create(payload);
      }
      navigate('/manager/goals');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : `Failed to ${isEditMode ? 'update' : 'create'} goal`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!goalId || !window.confirm(t('manager.goals.confirmDelete'))) return;

    setIsSubmitting(true);
    try {
      await deleteGoal(goalId);
      navigate('/manager/goals');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete goal');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleIconChange = (icon: IconName) => {
    setFormData({ ...formData, icon });
  };

  const isFormValid = (): boolean => {
    if (formData.config.amount === null || formData.config.amount < 1)
      return false;
    if (formData.rewardCard.length === 0) return false;
    if (!formData.title.trim()) return false;
    if (!formData.description.trim()) return false;
    if (formData.assignedTo.length === 0) return false;
    if (formData.config.selectors.length === 0) return false;

    // Check that all selectors have valid values
    for (const selector of formData.config.selectors) {
      if (selector.type === 'task' && !selector.task) return false;
      if (selector.type === 'achievement' && !selector.achievement)
        return false;
      if (selector.type === 'card' && !selector.card) return false;
    }

    return true;
  };

  return (
    <div className="bg-white min-vh-100">
      <Container fluid>
        <Row className="align-items-center bg-white p-4 pb-2">
          <Col xs="auto">
            <Button
              variant="link"
              className="p-0 text-dark"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft size={24} />
            </Button>
          </Col>
          <Col>
            <h2 className="h5 fw-bold mb-0">
              {isEditMode
                ? t('manager.goals.editGoal')
                : copyFromData
                  ? t('manager.goals.createGoalCopy')
                  : t('manager.goals.createGoal')}
            </h2>
          </Col>
        </Row>

        {isLoadingGoal || isLoadingData ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : (
          <Form onSubmit={handleSubmit} className="p-4">
            {error && (
              <Alert variant="danger" className="mb-4">
                <AlertCircle size={16} className="me-2" />
                {error}
              </Alert>
            )}

            <h6 className="fw-bold mb-3">{t('manager.goals.goalDetails')}</h6>

            <Form.Group className="mb-3">
              <Form.Label>{t('common.title')} *</Form.Label>
              <Form.Control
                type="text"
                value={formData.title}
                onChange={e =>
                  setFormData({ ...formData, title: e.target.value })
                }
                required
                placeholder={t('manager.goals.titlePlaceholder')}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('common.description')} *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.description}
                onChange={e =>
                  setFormData({ ...formData, description: e.target.value })
                }
                required
                placeholder={t('manager.goals.descriptionPlaceholder')}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('manager.goals.assignTo')} *</Form.Label>
              <MultiSelectDropdown
                options={users.map(user => ({
                  value: user._id,
                  label: `${user.given_name} ${user.family_name}`,
                  sublabel: user.email,
                }))}
                value={formData.assignedTo}
                onChange={selectedValues =>
                  setFormData({ ...formData, assignedTo: selectedValues })
                }
                placeholder={t('manager.goals.selectUser')}
                disabled={isLoadingData}
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>{t('common.icon')}</Form.Label>
              <IconSelector
                value={formData.icon}
                onChange={handleIconChange}
                className="w-100"
              />
            </Form.Group>

            <h6 className="fw-bold mb-3">{t('manager.goals.goalConfig')}</h6>

            <Form.Group className="mb-3">
              <Form.Label>{t('manager.goals.taskSelectors')} *</Form.Label>
              {formData.config.selectors.map((selector, index) => (
                <div key={index} className="border p-3 mb-2 rounded">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="fw-bold">
                      {t('manager.goals.selector')} {index + 1}
                    </span>
                    {formData.config.selectors.length > 1 && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => {
                          const newSelectors = formData.config.selectors.filter(
                            (_, i) => i !== index,
                          );
                          setFormData({
                            ...formData,
                            config: {
                              ...formData.config,
                              selectors: newSelectors,
                            },
                          });
                        }}
                      >
                        {t('common.remove')}
                      </Button>
                    )}
                  </div>

                  <Form.Group className="mb-2">
                    <Form.Label>{t('manager.goals.selectorType')}</Form.Label>
                    <Form.Select
                      value={selector.type}
                      onChange={e => {
                        const type = e.target.value as
                          | 'task'
                          | 'achievement'
                          | 'card';
                        const newSelectors = [...formData.config.selectors];
                        if (type === 'task') {
                          newSelectors[index] = { type: 'task', task: '' };
                        } else if (type === 'achievement') {
                          newSelectors[index] = {
                            type: 'achievement',
                            achievement: '',
                          };
                        } else if (type === 'card') {
                          newSelectors[index] = { type: 'card', card: '' };
                        }
                        setFormData({
                          ...formData,
                          config: {
                            ...formData.config,
                            selectors: newSelectors,
                          },
                        });
                      }}
                    >
                      <option value="task">
                        {t('manager.goals.taskBased')}
                      </option>
                      <option value="achievement">
                        {t('manager.goals.achievementBased')}
                      </option>
                      <option value="card">Card Collection</option>
                    </Form.Select>
                  </Form.Group>

                  {selector.type === 'task' ? (
                    <Form.Group>
                      <Form.Label>{t('manager.goals.selectTask')}</Form.Label>
                      <Form.Select
                        value={selector.task}
                        onChange={e => {
                          const newSelectors = [...formData.config.selectors];
                          newSelectors[index] = {
                            type: 'task',
                            task: e.target.value,
                          };
                          setFormData({
                            ...formData,
                            config: {
                              ...formData.config,
                              selectors: newSelectors,
                            },
                          });
                        }}
                        required
                      >
                        <option value="" disabled>
                          {t('manager.goals.selectTaskOption')}
                        </option>
                        {recurrences
                          .filter(recurrence => {
                            if (formData.assignedTo.length === 0) {
                              return true;
                            }
                            return recurrence.assignedTo.some((user: User) =>
                              formData.assignedTo.includes(user._id),
                            );
                          })
                          .map(recurrence => (
                            <option key={recurrence._id} value={recurrence._id}>
                              {recurrence.title}
                            </option>
                          ))}
                      </Form.Select>
                    </Form.Group>
                  ) : selector.type === 'achievement' ? (
                    <Form.Group>
                      <Form.Label>
                        {t('manager.goals.selectAchievement')}
                      </Form.Label>
                      <Form.Select
                        value={selector.achievement}
                        onChange={e => {
                          const newSelectors = [...formData.config.selectors];
                          newSelectors[index] = {
                            type: 'achievement',
                            achievement: e.target.value,
                          };
                          setFormData({
                            ...formData,
                            config: {
                              ...formData.config,
                              selectors: newSelectors,
                            },
                          });
                        }}
                        required
                      >
                        <option value="">
                          {t('manager.goals.selectAchievementOption')}
                        </option>
                        {goals
                          .filter(goal => {
                            if (formData.assignedTo.length === 0) {
                              return true;
                            }
                            return goal.assignedTo.some((user: User) =>
                              formData.assignedTo.includes(user._id),
                            );
                          })
                          .map(goal => (
                            <option key={goal._id} value={goal._id}>
                              {goal.title}
                            </option>
                          ))}
                      </Form.Select>
                    </Form.Group>
                  ) : (
                    <Form.Group>
                      <Form.Label>Select Card</Form.Label>
                      <Form.Select
                        value={selector.card || ''}
                        onChange={e => {
                          const newSelectors = [...formData.config.selectors];
                          newSelectors[index] = {
                            type: 'card',
                            card: e.target.value,
                          };
                          setFormData({
                            ...formData,
                            config: {
                              ...formData.config,
                              selectors: newSelectors,
                            },
                          });
                        }}
                        required
                      >
                        <option value="">Select a card...</option>
                        {cards.map(card => (
                          <option key={card._id} value={card._id}>
                            {card.title || 'Untitled Card'}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  )}
                </div>
              ))}

              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  const newSelectors = [
                    ...formData.config.selectors,
                    { type: 'task' as const, task: '' },
                  ];
                  setFormData({
                    ...formData,
                    config: {
                      ...formData.config,
                      selectors: newSelectors,
                    },
                  });
                }}
              >
                {t('manager.goals.addSelector')}
              </Button>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('manager.goals.sequenceType')} *</Form.Label>
              <Form.Select
                value={formData.config.sequenceType}
                onChange={e =>
                  setFormData({
                    ...formData,
                    config: {
                      ...formData.config,
                      sequenceType: e.target.value as
                        | 'sequential'
                        | 'cumulative',
                    },
                  })
                }
              >
                <option value="cumulative">{t('goals.cumulative')}</option>
                <option value="sequential">{t('goals.sequential')}</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('manager.goals.amount')} *</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={formData.config.amount ?? ''}
                onChange={e => {
                  const rawValue = e.target.value;
                  const value =
                    rawValue === ''
                      ? null
                      : Math.max(1, parseInt(rawValue, 10) || 1);
                  setFormData({
                    ...formData,
                    config: {
                      ...formData.config,
                      amount: value,
                    },
                  });
                }}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label={t('manager.goals.repeatable')}
                checked={formData.config.repeatable}
                onChange={e =>
                  setFormData({
                    ...formData,
                    config: {
                      ...formData.config,
                      repeatable: e.target.checked,
                    },
                  })
                }
              />
            </Form.Group>

            <h6 className="fw-bold mb-3">Reward Card</h6>

            <InlineCardEditor
              label="Reward Card"
              actions={formData.rewardCard}
              onChange={(actions: CardAction[]) =>
                setFormData({ ...formData, rewardCard: actions })
              }
              required
            />

            <div className="d-flex gap-2 mt-4">
              <Button
                variant="secondary"
                onClick={() => navigate(-1)}
                disabled={isSubmitting}
              >
                {t('common.cancel')}
              </Button>
              {isEditMode && (
                <Button
                  variant="danger"
                  onClick={handleDelete}
                  disabled={isSubmitting}
                >
                  {t('common.delete')}
                </Button>
              )}
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting || !isFormValid()}
                className="flex-grow-1"
              >
                {isSubmitting ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    {isEditMode
                      ? t('manager.goals.updating')
                      : t('manager.goals.creating')}
                  </>
                ) : isEditMode ? (
                  t('common.update')
                ) : (
                  t('common.create')
                )}
              </Button>
            </div>
          </Form>
        )}
      </Container>
    </div>
  );
}
